import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, ButtonSize, ButtonType, ButtonVariant } from '../buttons/Button';
import { BodyTwo } from '../headings/BodyTwo';
import styles from './EmptyState.module.css';

export type EmptyStateStatus = 'loading' | 'ready' | 'failed';

interface EmptyStateProps {
  message: string;
  /** Material icon shown above the message. */
  icon?: string;
  /** A line of help under the message, for example how to add the first item. */
  hint?: string;
  /** Label and handler of a button that adds the first item. */
  actionLabel?: string;
  onAction?: () => void;
  /**
   * 'loading' shows a spinner instead of the message, and 'failed' says the list could not be loaded
   * (with a retry button when onRetry is given). Defaults to 'ready', which shows the message.
   */
  status?: EmptyStateStatus;
  onRetry?: () => void;
}

interface CenteredProps {
  icon?: string;
  message: string;
  hint?: string;
  children?: ReactNode;
}

const Centered = ({ icon, message, hint, children }: CenteredProps) => (
  <div className="d-flex flex-column align-items-center text-center text-secondary py-5 px-3 gap-2" role="status">
    {icon && <span className="material-icons fs-1" aria-hidden="true">{icon}</span>}
    <div className="fw-semibold">{message}</div>
    {hint && <div className="small">{hint}</div>}
    {children}
  </div>
);

const Loading = () => {
  const { t } = useTranslation();
  return (
    <div className="d-flex justify-content-center align-items-center text-secondary py-5 gap-2" role="status">
      <span className="spinner-border spinner-border-sm" aria-hidden="true" />
      <span>{t('EMPTY_STATE.LOADING')}</span>
    </div>
  );
};

interface FailedProps {
  onRetry?: () => void;
}

const Failed = ({ onRetry }: FailedProps) => {
  const { t } = useTranslation();
  return (
    <Centered icon="error_outline" message={t('EMPTY_STATE.LOAD_FAILED')}>
      {onRetry && (
        <Button htmlType="button" size={ButtonSize.small} type={ButtonType.secondary} variant={ButtonVariant.outline} onClick={onRetry}>
          {t('EMPTY_STATE.RETRY')}
        </Button>
      )}
    </Centered>
  );
};

// With just a message it is the plain centred line it always was. Give it an icon, hint, action or status
// and it becomes the fuller state a list shows: loading, failed with retry, or empty with how to add the first item.
export const EmptyState = ({ message, icon, hint, actionLabel, onAction, status = 'ready', onRetry }: EmptyStateProps) => {
  if (status === 'loading') return <Loading />;
  if (status === 'failed') return <Failed onRetry={onRetry} />;

  const action = actionLabel && onAction ? { label: actionLabel, run: onAction } : null;
  if (!icon && !hint && !action) {
    return (
      <div className="col-12">
        <div className={`d-flex justify-content-center align-self-center ${styles.empty_state}`}>
          <BodyTwo content={message} />
        </div>
      </div>
    );
  }

  return (
    <Centered icon={icon} message={message} hint={hint}>
      {action && <Button htmlType="button" size={ButtonSize.small} onClick={action.run}>{action.label}</Button>}
    </Centered>
  );
};
